/**
 * IndexedDB (via idb) — the local-first source of truth for tracks (spec 03).
 * Two stores so list views never load point blobs: trackMeta + trackPoints.
 * A single liveTrack record gives crash safety while recording.
 */
import { openDB, type DBSchema, type IDBPDatabase } from 'idb';
import type { ThumbPoint, Bbox, TileCoord, FuelUnit } from '@boatnav/core';
import type { WeatherResponse, TideResponse, SearchHit, SettingsPayload } from '@boatnav/schemas';

export interface TrackMeta {
  id: string;
  name: string;
  startedAt: number;
  endedAt: number;
  distanceNm: number;
  durationS: number;
  avgKn: number;
  maxKn: number;
  pointCount: number;
  thumb: ThumbPoint[];
  updatedAt: number;
  deletedAt: number | null;
  /** 1 = pending sync, 0/undefined = synced (spec 08 outbox index). */
  dirty?: number;
}

export interface TrackPointsRecord {
  trackId: string;
  buf: ArrayBuffer;
}

export interface LiveTrackRecord {
  id: 'current';
  trackId: string;
  startedAt: number;
  buf: ArrayBuffer;
  pointCount: number;
  updatedAt: number;
}

export interface RegionMeta {
  id: string;
  name: string;
  bbox: Bbox;
  zMin: number;
  zMax: number;
  tileCount: number;
  bytes: number;
  createdAt: number;
  complete: boolean;
  /** Depth-tile dataset edition baked into this region's pack (spec 10);
   *  null for regions downloaded before depth packs existed, or if the
   *  edition was unknown (offline) at download time. */
  depthEdition: string | null;
}

/** A queued download tile tagged with the tile-proxy path it came from — a
 *  region pack fetches from more than one source (base chart + depth, spec
 *  10), so the source has to survive a checkpoint/resume round-trip. */
export interface DownloadTile extends TileCoord {
  basePath: string;
}

export interface RegionProgress {
  regionId: string;
  remaining: DownloadTile[];
  done: number;
  total: number;
}

/** Depth-tile dataset edition (spec 10), cached from `/depthtiles/meta` so a
 *  cold offline start still has something to compare against. */
export interface DepthMetaRecord {
  id: 'depthMeta';
  edition: string;
  updatedAt: number;
}

export interface Boat {
  id: string;
  name: string;
  draughtM: number;
  cruiseKn: number;
  fuelValue: number;
  fuelUnit: FuelUnit;
  tankL: number | null;
  /** Under-keel clearance for auto-routing (spec 09); default 0.5 m. */
  clearanceM: number;
  updatedAt: number;
  deletedAt: number | null;
  dirty?: number;
}

export interface Prefs {
  id: 'prefs';
  activeBoatId: string | null;
}

/** Last-fetched weather/tide reading (spec 11/12), persisted so an offline
 *  session still shows a stale-but-honest reading with a "sist oppdatert"
 *  label instead of an empty sheet. One record each — the sheet only ever
 *  needs the single most recent reading for own position. */
export interface WeatherRecord {
  id: 'weather';
  data: WeatherResponse;
  fetchedAt: number;
  atLat: number;
  atLon: number;
}

export interface TideRecord {
  id: 'tide';
  data: TideResponse;
  fetchedAt: number;
  atLat: number;
  atLon: number;
}

export interface SyncState {
  id: 'sync';
  lastServerSeq: number;
}

/**
 * The synced device-prefs document (spec 17) — one row, whole-doc LWW,
 * mirroring the six localStorage-backed pref stores. `updatedAt`/`dirty`
 * follow the same outbox pattern as boats/routes (spec 08); `localStorage`
 * stays the pre-paint read path for theme/text-scale (never regress boot).
 */
export interface SettingsRecord {
  id: 'settings';
  payload: SettingsPayload;
  updatedAt: number;
  /** 1 = pending sync, 0/undefined = synced (spec 08 outbox index). */
  dirty?: number;
}

/** A place the user selected from search (spec 13) — persisted so the last 10
 *  stay selectable with no net (the offline story for search). Keyed by
 *  rounded coordinates so re-selecting the same place refreshes its
 *  recency instead of duplicating the row. */
export interface SearchRecent extends SearchHit {
  id: string;
  selectedAt: number;
}

/** Live trip sharing (spec 15) — the write capability, persisted so a reload
 *  resumes beaconing. Never synced, never leaves this device except as the
 *  writeKey embedded in outgoing point-append calls. */
export interface ShareSessionRecord {
  id: 'current';
  sessionId: string;
  writeKey: string;
  expiresAt: number;
  boatName: string | null;
  createdAt: number;
}

export interface ShareBufferPoint {
  lat: number;
  lon: number;
  ts: number;
  sogKn: number | null;
  cogDeg: number | null;
}

/** Not-yet-flushed fixes — persisted so the offline buffer survives a reload
 *  (spec 15 acceptance). One record, capped by the caller (240 points/4h). */
export interface ShareBufferRecord {
  id: 'current';
  points: ShareBufferPoint[];
}

export interface Waypoint {
  lat: number;
  lon: number;
  name?: string;
}

export interface Route {
  id: string;
  name: string;
  waypoints: Waypoint[];
  updatedAt: number;
  deletedAt: number | null;
  dirty?: number;
}

interface BoatNavDB extends DBSchema {
  trackMeta: {
    key: string;
    value: TrackMeta;
    indexes: { startedAt: number; dirty: number };
  };
  trackPoints: {
    key: string;
    value: TrackPointsRecord;
  };
  liveTrack: {
    key: string;
    value: LiveTrackRecord;
  };
  regions: {
    key: string;
    value: RegionMeta;
  };
  regionProgress: {
    key: string;
    value: RegionProgress;
  };
  boats: {
    key: string;
    value: Boat;
    indexes: { dirty: number };
  };
  prefs: {
    key: string;
    value: Prefs;
  };
  routes: {
    key: string;
    value: Route;
    indexes: { updatedAt: number; dirty: number };
  };
  syncState: {
    key: string;
    value: SyncState;
  };
  depthMeta: {
    key: string;
    value: DepthMetaRecord;
  };
  weather: {
    key: string;
    value: WeatherRecord;
  };
  tide: {
    key: string;
    value: TideRecord;
  };
  searchRecents: {
    key: string;
    value: SearchRecent;
    indexes: { selectedAt: number };
  };
  shareSession: {
    key: string;
    value: ShareSessionRecord;
  };
  shareBuffer: {
    key: string;
    value: ShareBufferRecord;
  };
  settings: {
    key: string;
    value: SettingsRecord;
    indexes: { dirty: number };
  };
}

let dbPromise: Promise<IDBPDatabase<BoatNavDB>> | null = null;

function getDb(): Promise<IDBPDatabase<BoatNavDB>> {
  dbPromise ??= openDB<BoatNavDB>('boatnav', 10, {
    upgrade(db, oldVersion, _newVersion, tx) {
      if (oldVersion < 1) {
        const meta = db.createObjectStore('trackMeta', { keyPath: 'id' });
        meta.createIndex('startedAt', 'startedAt');
        db.createObjectStore('trackPoints', { keyPath: 'trackId' });
        db.createObjectStore('liveTrack', { keyPath: 'id' });
      }
      if (oldVersion < 2) {
        db.createObjectStore('regions', { keyPath: 'id' });
        db.createObjectStore('regionProgress', { keyPath: 'regionId' });
      }
      if (oldVersion < 3) {
        db.createObjectStore('boats', { keyPath: 'id' });
        db.createObjectStore('prefs', { keyPath: 'id' });
      }
      if (oldVersion < 4) {
        const routes = db.createObjectStore('routes', { keyPath: 'id' });
        routes.createIndex('updatedAt', 'updatedAt');
      }
      if (oldVersion < 5) {
        // Outbox indexes for sync (spec 08) + the sync cursor store.
        tx.objectStore('trackMeta').createIndex('dirty', 'dirty');
        tx.objectStore('boats').createIndex('dirty', 'dirty');
        tx.objectStore('routes').createIndex('dirty', 'dirty');
        db.createObjectStore('syncState', { keyPath: 'id' });
      }
      if (oldVersion < 6) {
        // Depth-tile dataset edition cache (spec 10) — existing `regions`
        // records simply read back with `depthEdition: undefined` (treated
        // as unknown/stale by callers), no migration needed for that field.
        db.createObjectStore('depthMeta', { keyPath: 'id' });
      }
      if (oldVersion < 7) {
        // Last-fetched weather/tide reading (spec 11/12) — one record each,
        // so a cold offline start still has something to show as stale.
        db.createObjectStore('weather', { keyPath: 'id' });
        db.createObjectStore('tide', { keyPath: 'id' });
      }
      if (oldVersion < 8) {
        // Recent search selections (spec 13) — last 10, offline-readable.
        const recents = db.createObjectStore('searchRecents', { keyPath: 'id' });
        recents.createIndex('selectedAt', 'selectedAt');
      }
      if (oldVersion < 9) {
        // Live trip sharing (spec 15): the write session + its offline
        // point buffer, so a reload resumes beaconing without re-sharing.
        db.createObjectStore('shareSession', { keyPath: 'id' });
        db.createObjectStore('shareBuffer', { keyPath: 'id' });
      }
      if (oldVersion < 10) {
        // Synced device prefs (spec 17): one whole-document row, outbox
        // pattern mirrors boats/routes (spec 08). localStorage (per pref
        // store) stays the source of truth for pre-paint reads; this store
        // is the sync-of-record.
        const settings = db.createObjectStore('settings', { keyPath: 'id' });
        settings.createIndex('dirty', 'dirty');
      }
    },
  });
  return dbPromise;
}

export async function saveFinalizedTrack(meta: TrackMeta, buf: ArrayBuffer): Promise<void> {
  const db = await getDb();
  const tx = db.transaction(['trackMeta', 'trackPoints'], 'readwrite');
  await Promise.all([
    tx.objectStore('trackMeta').put({ ...meta, dirty: 1 }),
    tx.objectStore('trackPoints').put({ trackId: meta.id, buf }),
    tx.done,
  ]);
}

/** Newest first, excluding tombstones. `before` paginates by startedAt. */
export async function listTracks(limit = 20, before?: number): Promise<TrackMeta[]> {
  const db = await getDb();
  const out: TrackMeta[] = [];
  const range = before != null ? IDBKeyRange.upperBound(before, true) : undefined;
  let cursor = await db
    .transaction('trackMeta')
    .objectStore('trackMeta')
    .index('startedAt')
    .openCursor(range, 'prev');
  while (cursor && out.length < limit) {
    if (cursor.value.deletedAt == null) out.push(cursor.value);
    cursor = await cursor.continue();
  }
  return out;
}

export async function getTrackMeta(id: string): Promise<TrackMeta | undefined> {
  return (await getDb()).get('trackMeta', id);
}

export async function getTrackPoints(id: string): Promise<ArrayBuffer | undefined> {
  const rec = await (await getDb()).get('trackPoints', id);
  return rec?.buf;
}

export async function renameTrack(id: string, name: string): Promise<void> {
  const db = await getDb();
  const meta = await db.get('trackMeta', id);
  if (!meta) return;
  meta.name = name;
  meta.updatedAt = Date.now();
  meta.dirty = 1;
  await db.put('trackMeta', meta);
}

/** Soft delete (tombstone for sync); drops the point blob to reclaim space. */
export async function deleteTrack(id: string): Promise<void> {
  const db = await getDb();
  const meta = await db.get('trackMeta', id);
  if (!meta) return;
  meta.deletedAt = Date.now();
  meta.updatedAt = meta.deletedAt;
  meta.dirty = 1;
  const tx = db.transaction(['trackMeta', 'trackPoints'], 'readwrite');
  await Promise.all([
    tx.objectStore('trackMeta').put(meta),
    tx.objectStore('trackPoints').delete(id),
    tx.done,
  ]);
}

// --- liveTrack (crash safety) ---

export async function saveLiveTrack(rec: Omit<LiveTrackRecord, 'id'>): Promise<void> {
  await (await getDb()).put('liveTrack', { id: 'current', ...rec });
}

export async function getLiveTrack(): Promise<LiveTrackRecord | undefined> {
  return (await getDb()).get('liveTrack', 'current');
}

export async function clearLiveTrack(): Promise<void> {
  await (await getDb()).delete('liveTrack', 'current');
}

// --- regions (offline chart packs) ---

export async function listRegions(): Promise<RegionMeta[]> {
  const all = await (await getDb()).getAll('regions');
  return all.sort((a, b) => b.createdAt - a.createdAt);
}

export async function getRegion(id: string): Promise<RegionMeta | undefined> {
  return (await getDb()).get('regions', id);
}

export async function putRegion(region: RegionMeta): Promise<void> {
  await (await getDb()).put('regions', region);
}

export async function deleteRegionMeta(id: string): Promise<void> {
  const db = await getDb();
  const tx = db.transaction(['regions', 'regionProgress'], 'readwrite');
  await Promise.all([
    tx.objectStore('regions').delete(id),
    tx.objectStore('regionProgress').delete(id),
    tx.done,
  ]);
}

export async function saveProgress(progress: RegionProgress): Promise<void> {
  await (await getDb()).put('regionProgress', progress);
}

export async function getProgress(regionId: string): Promise<RegionProgress | undefined> {
  return (await getDb()).get('regionProgress', regionId);
}

export async function clearProgress(regionId: string): Promise<void> {
  await (await getDb()).delete('regionProgress', regionId);
}

// --- depth-tile dataset edition (spec 10) ---

export async function getDepthMeta(): Promise<DepthMetaRecord | undefined> {
  return (await getDb()).get('depthMeta', 'depthMeta');
}

export async function putDepthMeta(edition: string): Promise<void> {
  await (await getDb()).put('depthMeta', { id: 'depthMeta', edition, updatedAt: Date.now() });
}

// --- boats + prefs (spec 07) ---

/** Coerce a value to a finite number, else a fallback. */
function finiteOr(value: unknown, fallback: number): number {
  const n = Number(value);
  return Number.isFinite(n) ? n : fallback;
}

/**
 * Heal non-finite numeric fields to sane defaults. Partial or corrupt records
 * (e.g. a boat synced before `clearanceM` existed) must never surface as NaN in
 * the form or the routing math.
 */
export function normalizeBoat(b: Boat): Boat {
  return {
    ...b,
    draughtM: finiteOr(b.draughtM, 1.2),
    cruiseKn: finiteOr(b.cruiseKn, 20),
    fuelValue: finiteOr(b.fuelValue, 1.1),
    tankL: b.tankL != null && Number.isFinite(Number(b.tankL)) ? Number(b.tankL) : null,
    clearanceM: finiteOr(b.clearanceM, 0.5),
  };
}

export async function listBoats(): Promise<Boat[]> {
  const all = await (await getDb()).getAll('boats');
  return all
    .filter((b) => b.deletedAt == null)
    .map(normalizeBoat)
    .sort((a, b) => a.name.localeCompare(b.name, 'nb'));
}

export async function putBoat(boat: Boat): Promise<void> {
  await (await getDb()).put('boats', { ...boat, dirty: 1 });
}

export async function deleteBoat(id: string): Promise<void> {
  const db = await getDb();
  const boat = await db.get('boats', id);
  if (!boat) return;
  boat.deletedAt = Date.now();
  boat.updatedAt = boat.deletedAt;
  boat.dirty = 1;
  await db.put('boats', boat);
}

export async function getPrefs(): Promise<Prefs> {
  const p = await (await getDb()).get('prefs', 'prefs');
  return p ?? { id: 'prefs', activeBoatId: null };
}

export async function putPrefs(prefs: Prefs): Promise<void> {
  await (await getDb()).put('prefs', prefs);
}

// --- routes (spec 05) ---

export async function listRoutes(): Promise<Route[]> {
  const all = await (await getDb()).getAll('routes');
  return all.filter((r) => r.deletedAt == null).sort((a, b) => b.updatedAt - a.updatedAt);
}

export async function getRoute(id: string): Promise<Route | undefined> {
  return (await getDb()).get('routes', id);
}

export async function putRoute(route: Route): Promise<void> {
  await (await getDb()).put('routes', { ...route, dirty: 1 });
}

export async function deleteRoute(id: string): Promise<void> {
  const db = await getDb();
  const route = await db.get('routes', id);
  if (!route) return;
  route.deletedAt = Date.now();
  route.updatedAt = route.deletedAt;
  route.dirty = 1;
  await db.put('routes', route);
}

// --- settings (spec 17) — synced device prefs, one whole-doc row ---

export async function getSettingsRecord(): Promise<SettingsRecord | undefined> {
  return (await getDb()).get('settings', 'settings');
}

/** Write the combined prefs doc locally. `dirty` defaults to 1 (a local edit);
 *  the settingsSync bridge (spec 17) passes 0 when persisting an applied pull. */
export async function putSettingsRecord(payload: SettingsPayload, updatedAt = Date.now(), dirty = 1): Promise<void> {
  await (await getDb()).put('settings', { id: 'settings', payload, updatedAt, dirty });
}

// --- sync outbox + cursor (spec 08) ---

export type SyncEntity = 'boat' | 'route' | 'track' | 'settings';

export interface LocalChangeRow {
  entity: SyncEntity;
  id: string;
  updatedAt: number;
  deleted: boolean;
  payload?: Record<string, unknown>;
}

function boatPayload(b: Boat): Record<string, unknown> {
  return {
    name: b.name,
    draughtM: b.draughtM,
    cruiseKn: b.cruiseKn,
    fuelValue: b.fuelValue,
    fuelUnit: b.fuelUnit,
    tankL: b.tankL,
    clearanceM: b.clearanceM,
  };
}
function routePayload(r: Route): Record<string, unknown> {
  return { name: r.name, waypoints: r.waypoints };
}
function trackPayload(t: TrackMeta): Record<string, unknown> {
  return {
    name: t.name,
    startedAt: t.startedAt,
    endedAt: t.endedAt,
    distanceNm: t.distanceNm,
    durationS: t.durationS,
    avgKn: t.avgKn,
    maxKn: t.maxKn,
    pointCount: t.pointCount,
    thumb: t.thumb,
  };
}

export async function collectDirty(): Promise<LocalChangeRow[]> {
  const db = await getDb();
  const out: LocalChangeRow[] = [];
  const range = IDBKeyRange.only(1);

  for (const b of await db.getAllFromIndex('boats', 'dirty', range)) {
    out.push({
      entity: 'boat',
      id: b.id,
      updatedAt: b.updatedAt,
      deleted: b.deletedAt != null,
      payload: b.deletedAt != null ? undefined : boatPayload(b),
    });
  }
  for (const r of await db.getAllFromIndex('routes', 'dirty', range)) {
    out.push({
      entity: 'route',
      id: r.id,
      updatedAt: r.updatedAt,
      deleted: r.deletedAt != null,
      payload: r.deletedAt != null ? undefined : routePayload(r),
    });
  }
  for (const t of await db.getAllFromIndex('trackMeta', 'dirty', range)) {
    out.push({
      entity: 'track',
      id: t.id,
      updatedAt: t.updatedAt,
      deleted: t.deletedAt != null,
      payload: t.deletedAt != null ? undefined : trackPayload(t),
    });
  }
  const settings = await db.get('settings', 'settings');
  if (settings?.dirty === 1) {
    out.push({
      entity: 'settings',
      id: 'settings',
      updatedAt: settings.updatedAt,
      deleted: false, // settings are never deleted (spec 17)
      payload: settings.payload,
    });
  }
  return out;
}

export async function markSynced(entity: SyncEntity, id: string): Promise<void> {
  const db = await getDb();
  if (entity === 'settings') {
    const row = await db.get('settings', 'settings');
    if (!row) return;
    row.dirty = 0;
    await db.put('settings', row);
    return;
  }
  const store = entity === 'boat' ? 'boats' : entity === 'route' ? 'routes' : 'trackMeta';
  const row = await db.get(store, id);
  if (!row) return;
  row.dirty = 0;
  await db.put(store, row);
}

/** Apply an incoming server change with a local LWW guard (spec 08).
 *  `'settings'` is handled separately by the settingsSync bridge (spec 17) —
 *  the syncService adapter routes that entity there before reaching here. */
export async function applyServerChange(change: {
  entity: Exclude<SyncEntity, 'settings'>;
  id: string;
  updatedAt: number;
  deleted: boolean;
  payload: Record<string, unknown> | null;
}): Promise<void> {
  const db = await getDb();
  const store = change.entity === 'boat' ? 'boats' : change.entity === 'route' ? 'routes' : 'trackMeta';
  const existing = await db.get(store, change.id);
  // Don't clobber a locally-newer edit.
  if (existing && existing.updatedAt > change.updatedAt) return;

  const base = { id: change.id, updatedAt: change.updatedAt, deletedAt: change.deleted ? change.updatedAt : null, dirty: 0 };
  const p = change.payload ?? {};

  if (change.entity === 'boat') {
    await db.put('boats', {
      ...base,
      name: String(p.name ?? ''),
      draughtM: finiteOr(p.draughtM, 1.2),
      cruiseKn: finiteOr(p.cruiseKn, 20),
      fuelValue: finiteOr(p.fuelValue, 1.1),
      fuelUnit: (p.fuelUnit as Boat['fuelUnit']) ?? 'l_per_nm',
      tankL: p.tankL != null && Number.isFinite(Number(p.tankL)) ? Number(p.tankL) : null,
      clearanceM: finiteOr(p.clearanceM, 0.5),
    });
  } else if (change.entity === 'route') {
    await db.put('routes', {
      ...base,
      name: String(p.name ?? ''),
      waypoints: (p.waypoints as Route['waypoints']) ?? [],
    });
  } else {
    await db.put('trackMeta', {
      ...base,
      name: String(p.name ?? ''),
      startedAt: Number(p.startedAt ?? 0),
      endedAt: Number(p.endedAt ?? 0),
      distanceNm: Number(p.distanceNm ?? 0),
      durationS: Number(p.durationS ?? 0),
      avgKn: Number(p.avgKn ?? 0),
      maxKn: Number(p.maxKn ?? 0),
      pointCount: Number(p.pointCount ?? 0),
      thumb: (p.thumb as TrackMeta['thumb']) ?? [],
    });
  }
}

export async function getSyncCursor(): Promise<number> {
  const s = await (await getDb()).get('syncState', 'sync');
  return s?.lastServerSeq ?? 0;
}

export async function setSyncCursor(seq: number): Promise<void> {
  await (await getDb()).put('syncState', { id: 'sync', lastServerSeq: seq });
}

/** First-login merge: mark every local row dirty so it pushes (spec 06/08).
 *  Includes the settings row (spec 17) if one already exists locally — a
 *  device that never touched a pref has nothing to push, and just pulls the
 *  account's settings instead; either way it converges (LWW). */
export async function markAllDirty(): Promise<void> {
  const db = await getDb();
  for (const store of ['boats', 'routes', 'trackMeta', 'settings'] as const) {
    const tx = db.transaction(store, 'readwrite');
    let cursor = await tx.store.openCursor();
    while (cursor) {
      await cursor.update({ ...cursor.value, dirty: 1 });
      cursor = await cursor.continue();
    }
    await tx.done;
  }
}

// --- weather / tide (spec 11/12) ---

export async function getWeatherRecord(): Promise<WeatherRecord | undefined> {
  return (await getDb()).get('weather', 'weather');
}

export async function putWeatherRecord(rec: Omit<WeatherRecord, 'id'>): Promise<void> {
  await (await getDb()).put('weather', { id: 'weather', ...rec });
}

export async function getTideRecord(): Promise<TideRecord | undefined> {
  return (await getDb()).get('tide', 'tide');
}

export async function putTideRecord(rec: Omit<TideRecord, 'id'>): Promise<void> {
  await (await getDb()).put('tide', { id: 'tide', ...rec });
}

export async function clearTideRecord(): Promise<void> {
  await (await getDb()).delete('tide', 'tide');
}

// --- search recents (spec 13) ---

const SEARCH_RECENTS_CAP = 10;

function searchRecentId(lat: number, lon: number): string {
  return `${lat.toFixed(5)},${lon.toFixed(5)}`;
}

/** Newest first, capped at 10 — the offline story for search (spec 13). */
export async function listSearchRecents(): Promise<SearchRecent[]> {
  const all = await (await getDb()).getAllFromIndex('searchRecents', 'selectedAt');
  return all.reverse().slice(0, SEARCH_RECENTS_CAP);
}

/** Record a selection, refreshing recency if the same place is picked again,
 *  then trim back down to the cap. */
export async function pushSearchRecent(hit: SearchHit): Promise<void> {
  const db = await getDb();
  const id = searchRecentId(hit.lat, hit.lon);
  await db.put('searchRecents', { ...hit, id, selectedAt: Date.now() });

  const all = await db.getAllFromIndex('searchRecents', 'selectedAt');
  const excess = all.length - SEARCH_RECENTS_CAP;
  if (excess > 0) {
    const tx = db.transaction('searchRecents', 'readwrite');
    for (const stale of all.slice(0, excess)) await tx.store.delete(stale.id);
    await tx.done;
  }
}

// --- live trip sharing (spec 15) ---

export async function getShareSession(): Promise<ShareSessionRecord | undefined> {
  return (await getDb()).get('shareSession', 'current');
}

export async function putShareSession(rec: Omit<ShareSessionRecord, 'id'>): Promise<void> {
  await (await getDb()).put('shareSession', { id: 'current', ...rec });
}

export async function clearShareSession(): Promise<void> {
  await (await getDb()).delete('shareSession', 'current');
}

export async function getShareBuffer(): Promise<ShareBufferPoint[]> {
  const rec = await (await getDb()).get('shareBuffer', 'current');
  return rec?.points ?? [];
}

export async function putShareBuffer(points: ShareBufferPoint[]): Promise<void> {
  await (await getDb()).put('shareBuffer', { id: 'current', points });
}

export async function clearShareBuffer(): Promise<void> {
  await (await getDb()).delete('shareBuffer', 'current');
}
